'use client'

import { useCartStore } from '@/store/cartStore'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SheetClose, SheetFooter } from '@/components/ui/sheet'
import { ShoppingBag } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRTLAwareStyle } from '@/util/rtl'
import { CartContent } from './CartContent'
import Link from 'next/link'

export default function CartSheet() {
  const { items, getTotalPrice, getTotalItems } = useCartStore()
  
  const t = useTranslations('cart')
  const rtlAlign = useRTLAwareStyle('text-left', 'text-right')
  const letterSpacing = useRTLAwareStyle('tracking-widest', '')

  const formatPrice = (price: number) => t('currency', { amount: price.toFixed(2) })

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <ShoppingBag className="w-16 h-16 text-[#bcb398] mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t('emptyCart')}</h2>
        <p className="text-sm text-gray-500 mb-6">{t('emptyCartDescription')}</p>
        <SheetClose asChild>
          <Button className="bg-[#bcb398] hover:bg-[#a39a82] text-white">{t('continueShopping')}</Button>
        </SheetClose>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full mt-6">
      <div className={rtlAlign}>
        <h2 className={`text-lg font-bold ${letterSpacing}`}>{t('yourCart')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('itemsInCart', { count: getTotalItems() })}
        </p>
      </div>
      <ScrollArea className="flex-1">
        <CartContent variant="sheet" />
      </ScrollArea>
      <SheetFooter className="">
        <div className={`mt-6 w-full ${rtlAlign}`}>
          <div className="flex justify-between text-base font-medium">
            <p>{t('subtotal')}</p>
            <p>{formatPrice(getTotalPrice())}</p>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('shippingAndTaxes')}</p>
          <div className="mt-6">
            <SheetClose asChild>
              <Link href='/cart' className='w-full'>
                <Button className="w-full bg-[#bcb398] hover:bg-[#a39a82] text-white">
                  {t('checkout')}
                </Button>
              </Link>
            </SheetClose>
          </div>
          <div className="mt-6 flex justify-center text-center text-sm text-muted-foreground">
            <p>
              {t('or')}{' '}
              <SheetClose asChild>
                <Button variant="link" className="text-sm font-medium text-[#bcb398] hover:text-[#a39a82]">
                  {t('continueShopping')}
                  <span aria-hidden="true"> &rarr;</span>
                </Button>
              </SheetClose>
            </p>
          </div>
        </div>
      </SheetFooter>
    </div>
  )
}